/**
 * Вкладка «Техника» целиком: состояние, запросы, диалоги (AK-747).
 *
 * Собрано отдельным компонентом, а не внутри карточки хозяйства: карточка уже
 * держит состояние трёх вкладок, и четвёртая порция фильтров/выделения в том
 * же файле сделала бы его нечитаемым. Наружу торчит ровно то, что карточке
 * нужно знать, — `farmId`, имя хозяйства и право на запись.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ARCHIVE_BATCH_MAX, capBatchSelection } from '@/lib/bulk/batchLimits';
import { ApiError, api } from '@/lib/api';
import { useTextFilterDraft } from '@/lib/filters/useTextFilterDraft';
import { toast } from '@/lib/toast';
import { useFarms } from '../../hooks/useStructureQueries';
import { farmCardKeys, useFarmSummary } from '../queries';
import { AddMachineryDialog, type MachineryDraft } from './AddMachineryDialog';
import { ImportMachineryDialog } from './ImportMachineryDialog';
import { ReassignMachineryDialog } from './ReassignMachineryDialog';
import { WriteOffMachineryDialog } from './WriteOffMachineryDialog';
import { MachineryTab } from './MachineryTab';
import {
  machineryBulkOutcome,
  machineryPlural,
  type BulkResult,
  type FleetFreshness,
  type MachineryCounts,
  type MachineryRow,
  type MachineryView,
} from './machineryLogic';
import { useFarmMachinery, useFleetFreshness, useMachineryMutation } from './queries';

/**
 * Текст отказа при заведении машины.
 *
 * Сообщение с сервера показываем ДОСЛОВНО, когда оно осмысленное: бэкенд
 * знает, что именно не так («гос. номер А 123 ВС 61 уже есть в парке»), и
 * переписывать это на клиенте общей фразой значит терять единственный факт,
 * ради которого человек читает ошибку.
 */
export function addFailureMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = err.rawDetail as { message?: string } | undefined;
    if (detail?.message) return detail.message;
    if (err.status === 409) return 'Такая машина уже есть в парке компании.';
    if (err.status === 403) return 'Нет прав заводить технику — обратитесь к администратору хозяйства.';
  }
  return 'Не получилось добавить — проверьте поля и попробуйте ещё раз.';
}

export function FarmMachinerySection({
  farmId,
  farmName,
  canWrite,
  counts,
  view,
  onViewChange,
  openImport = false,
}: {
  farmId: string;
  farmName: string;
  canWrite: boolean;
  /** Мастер загрузки списком открыт ССЫЛКОЙ извне (`?action=import`) — так на
   *  вкладку приходят из шага «Техника» подготовки хозяйства (AK-804). */
  openImport?: boolean;
  /** Счётчики приходят сверху: их уже запросила карточка ради бейджа вкладки,
   *  и второй раз спрашивать сервер о том же незачем. */
  counts: MachineryCounts | undefined;
  /** Под-вкладка живёт в АДРЕСЕ (`?subtab=`) — F5 и пересланная ссылка
   *  обязаны вернуть человека туда же, где он был. */
  view: MachineryView;
  onViewChange: (view: MachineryView) => void;
}) {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(openImport && canWrite);
  const queryClient = useQueryClient();
  const [reassignOpen, setReassignOpen] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);

  const query = useMemo(
    () => ({ farmId, search, group, includeArchived }),
    [farmId, search, group, includeArchived],
  );
  const listQuery = useFarmMachinery(query, view === 'units');
  const pages = listQuery.data?.pages;
  const rows: MachineryRow[] = useMemo(() => pages?.flatMap((p) => p.items) ?? [], [pages]);
  // Итог берём из ПЕРВОЙ страницы: сервер считает его по всей выборке под
  // фильтром, а не по отданным строкам.
  const total = pages?.[0]?.total ?? 0;

  // Хозяйства для перегона берём из общего дерева структуры — второго списка
  // хозяйств под то же самое не заводим.
  const { data: farms = [] } = useFarms(false);

  /** Отметки сбрасываем на любой смене выборки: иначе пачка уедет по строкам,
   *  которых человек уже не видит. */
  const resetSelection = useCallback(() => setSelected(new Set()), []);

  // Поиск с паузой — общий хук, свой дребезг не изобретаем. Без паузы каждая
  // буква уходила запросом И сбрасывала отметки: человек терял выбор, пока
  // дописывал слово.
  const pushSearch = useCallback(
    (value: string) => {
      setSearch(value);
      resetSelection();
    },
    [resetSelection],
  );
  const [searchDraft, setSearchDraft] = useTextFilterDraft(search, pushSearch);

  const pickedIds = useMemo(
    () => capBatchSelection(rows.filter((r) => selected.has(r.id)).map((r) => r.id)),
    [rows, selected],
  );

  // Отделения берём из сводки карточки — она уже загружена ради вкладок, и
  // второго списка отделений под ту же форму не заводим.
  const summaryQuery = useFarmSummary(farmId);
  const brigades = useMemo(() => summaryQuery.data?.brigades ?? [], [summaryQuery.data]);

  const createMutation = useMachineryMutation(farmId, (draft: MachineryDraft) => {
    // Поля со своей колонкой (мощность, расход) разворачиваем в КОРЕНЬ тела:
    // в паспорте сервер их не принимает — у них есть honest-колонка.
    const { columns, ...rest } = draft;
    return api.post('/api/v1/company/machinery', { ...rest, ...columns, farm_id: farmId });
  });
  const archiveMutation = useMachineryMutation(
    farmId,
    ({ ids, reason }: { ids: string[]; reason: string }) =>
      api.post<BulkResult>('/api/v1/company/machinery/archive', { ids, reason }),
  );
  const confirmMutation = useMachineryMutation(farmId, () =>
    api.post<FleetFreshness>('/api/v1/company/machinery/confirm-fleet', { farm_id: farmId }),
  );
  const restoreMutation = useMachineryMutation(farmId, (ids: string[]) =>
    api.post<BulkResult>('/api/v1/company/machinery/restore', { ids }),
  );
  const reassignMutation = useMachineryMutation(
    farmId,
    ({ ids, targetFarmId }: { ids: string[]; targetFarmId: string }) =>
      api.post<BulkResult>('/api/v1/company/machinery/reassign', {
        ids,
        farm_id: targetFarmId,
      }),
  );

  const freshnessQuery = useFleetFreshness(farmId, view === 'units');

  const busy =
    createMutation.isPending ||
    archiveMutation.isPending ||
    restoreMutation.isPending ||
    reassignMutation.isPending;

  const runBulk = async (
    ids: string[],
    action: (list: string[]) => Promise<BulkResult>,
    done: string,
  ) => {
    if (!ids.length) return;
    try {
      const result = await action(ids);
      // ТОН по исходу, а не всегда success: `success` гаснет через четыре
      // секунды, и список неповезших машин исчез бы ровно тогда, когда он
      // человеку и нужен (правило `bulkActions.bulkOutcome` карточки полей).
      const outcome = machineryBulkOutcome(result, done, `Ничего не ${done.toLowerCase()}`, rows);
      toast[outcome.kind](outcome.message);
    } catch {
      toast.error('Не получилось — попробуйте ещё раз или обновите страницу.');
    } finally {
      resetSelection();
    }
  };

  return (
    <>
      <MachineryTab
        farmId={farmId}
        view={view}
        onViewChange={onViewChange}
        rows={rows}
        total={total}
        counts={counts}
        loading={listQuery.isLoading}
        hasMore={Boolean(listQuery.hasNextPage)}
        loadingMore={listQuery.isFetchingNextPage}
        onLoadMore={() => void listQuery.fetchNextPage()}
        search={searchDraft}
        onSearchChange={setSearchDraft}
        group={group}
        onGroupChange={(value) => {
          setGroup(value);
          resetSelection();
        }}
        includeArchived={includeArchived}
        onIncludeArchivedChange={(value) => {
          setIncludeArchived(value);
          resetSelection();
        }}
        selected={selected}
        onToggleRow={(id) =>
          setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        onToggleAll={(ids, checked) => setSelected(checked ? new Set(ids) : new Set())}
        canWrite={canWrite}
        batchMax={ARCHIVE_BATCH_MAX}
        busy={busy}
        onAdd={() => setAddOpen(true)}
        onImport={() => setImportOpen(true)}
        // «Списать» больше не действие в один клик: сначала спрашиваем причину
        // (Р22), иначе через месяц по парку не отличить проданное от дубля.
        onArchive={() => setWriteOffOpen(true)}
        onRestore={() =>
          void runBulk(pickedIds, (ids) => restoreMutation.mutateAsync(ids), 'Возвращено в парк')
        }
        onReassign={() => setReassignOpen(true)}
        freshness={freshnessQuery.data}
        confirming={confirmMutation.isPending}
        onConfirmFleet={async () => {
          try {
            await confirmMutation.mutateAsync(undefined);
            toast.success(
              rows.length === 0
                ? 'Отметили: техники в хозяйстве нет.'
                : 'Отметили: парк актуален на сегодня.',
            );
          } catch {
            toast.error('Не получилось отметить — попробуйте ещё раз или обновите страницу.');
          }
        }}
      />

      <AddMachineryDialog
        open={addOpen}
        farmName={farmName}
        brigades={brigades}
        pending={createMutation.isPending}
        onCancel={() => setAddOpen(false)}
        onSave={async (draft, keepOpen) => {
          try {
            await createMutation.mutateAsync(draft);
            if (!keepOpen) setAddOpen(false);
            // «Добавить ещё» тоже отчитывается: молчаливое сохранение в форме,
            // которая осталась открытой, читается как «ничего не произошло».
            // Пачку называем числом — «машина в парке» после заведения десяти
            // единиц читается как «поехала одна».
            const added =
              draft.quantity > 1
                ? `В парк встало ${machineryPlural(draft.quantity)}.`
                : 'Машина в парке хозяйства.';
            toast.success(keepOpen ? `${added} Форма готова к следующей.` : added);
            return true;
          } catch (err) {
            toast.error(addFailureMessage(err));
            return false;
          }
        }}
      />

      <ImportMachineryDialog
        open={importOpen}
        farmId={farmId}
        farmName={farmName}
        onClose={() => setImportOpen(false)}
        // Парк и счётчики перечитываем ТОЙ ЖЕ инвалидацией, что и остальные
        // действия вкладки: шапка, спорящая с таблицей, — худшее из состояний.
        onFinished={() => void queryClient.invalidateQueries({ queryKey: farmCardKeys.all(farmId) })}
      />

      <WriteOffMachineryDialog
        open={writeOffOpen}
        count={pickedIds.length}
        pending={archiveMutation.isPending}
        onCancel={() => setWriteOffOpen(false)}
        onConfirm={async (reason) => {
          setWriteOffOpen(false);
          await runBulk(
            pickedIds,
            (ids) => archiveMutation.mutateAsync({ ids, reason }),
            'Списано',
          );
        }}
      />

      <ReassignMachineryDialog
        open={reassignOpen}
        count={pickedIds.length}
        farms={farms}
        currentFarmId={farmId}
        pending={reassignMutation.isPending}
        onCancel={() => setReassignOpen(false)}
        onConfirm={async (targetFarmId) => {
          setReassignOpen(false);
          await runBulk(
            pickedIds,
            (ids) => reassignMutation.mutateAsync({ ids, targetFarmId }),
            'Перегнано',
          );
        }}
      />
    </>
  );
}
